import FieldHistoryIndicator from '@ors/components/ui/FieldHistoryIndicator/FieldHistoryIndicator'
import {
  getFieldExtraLabel,
  getFormattedNumericValue,
  hasExcomUpdate,
} from '../utils'
import {
  DetailItemClassname,
  FieldType,
  ProjectSpecificFields,
  ViewModesHandler,
} from '../interfaces'
import type { ProjectFieldHistoryValue } from '@ors/types/store'

import { find, isBoolean, upperFirst } from 'lodash'
import cx from 'classnames'
import dayjs from 'dayjs'

export type detailItemExtra = {
  detailClassname?: string
  classNames?: DetailItemClassname
  fieldHistory?: ProjectFieldHistoryValue[]
  isDisabledImpactField?: boolean
  isActual?: boolean
}

const getIsDisabledImpactField = (
  field: ProjectSpecificFields,
  hasActualFields?: boolean,
) => field.section === 'Impact' && hasActualFields && !field.is_actual

export const detailItem = (
  fieldName: string,
  fieldValue: string,
  extra?: detailItemExtra,
) => {
  const {
    detailClassname,
    classNames,
    fieldHistory = [],
    isDisabledImpactField,
    isActual,
  } = extra ?? {}
  const {
    containerClassName = '',
    className = '',
    fieldClassName = '',
  } = classNames ?? {}

  return fieldHistory && hasExcomUpdate(fieldHistory, fieldName) ? (
    <FieldHistoryIndicator
      dataType="text"
      history={fieldHistory}
      {...{ fieldName, extra }}
    />
  ) : (
    <span
      className={cx('flex gap-2', containerClassName, {
        italic: isDisabledImpactField,
        'font-medium': isActual,
      })}
    >
      <span className={cx(detailClassname, className)}>
        {fieldName}
        {getFieldExtraLabel(isDisabledImpactField, fieldName)}
      </span>
      <h4 className={cx('m-0', fieldClassName)}>{fieldValue || '-'}</h4>
    </span>
  )
}

export const numberDetailItem = (
  fieldName: string,
  fieldValue: string,
  dataType: string,
  fieldHistory?: detailItemExtra['fieldHistory'],
  isDisabledImpactField?: boolean,
  isActual?: boolean,
) =>
  fieldHistory && hasExcomUpdate(fieldHistory, fieldName) ? (
    <FieldHistoryIndicator
      history={fieldHistory}
      {...{ fieldName, dataType }}
    />
  ) : (
    <span
      className={cx('flex gap-2', {
        italic: isDisabledImpactField,
        'font-medium': isActual,
      })}
    >
      <span>
        {fieldName} {getFieldExtraLabel(isDisabledImpactField, fieldName)}
      </span>
      <h4 className="m-0">
        {getFormattedNumericValue(fieldValue, dataType === 'decimal' ? 2 : 0)}
      </h4>
    </span>
  )

export const booleanDetailItem = (
  fieldName: string,
  fieldValue: boolean | null | undefined,
  fieldHistory?: detailItemExtra['fieldHistory'],
  className?: string,
  isDisabledImpactField?: boolean,
  isActual?: boolean,
) =>
  fieldHistory && hasExcomUpdate(fieldHistory, fieldName) ? (
    <FieldHistoryIndicator
      dataType="boolean"
      history={fieldHistory}
      {...{ fieldName, className }}
    />
  ) : (
    <span
      className={cx('flex gap-2', className, {
        italic: isDisabledImpactField,
        'font-medium': isActual,
      })}
    >
      <span>
        {fieldName} {getFieldExtraLabel(isDisabledImpactField, fieldName)}
      </span>
      <h4 className="m-0">
        {fieldValue == null ? '-' : fieldValue ? 'Yes' : 'No'}
      </h4>
    </span>
  )

export const dateDetailItem = (
  fieldName: string,
  fieldValue: string,
  fieldHistory?: detailItemExtra['fieldHistory'],
) =>
  fieldHistory && hasExcomUpdate(fieldHistory, fieldName) ? (
    <FieldHistoryIndicator
      dataType="date"
      history={fieldHistory}
      fieldName={fieldName}
    />
  ) : (
    <span className="flex gap-2">
      <span>{fieldName}</span>
      <h4 className="m-0">
        {(fieldValue && dayjs(fieldValue).format('DD/MM/YYYY')) || '-'}
      </h4>
    </span>
  )

export const aprDateDetailItem = (
  fieldName: string,
  fieldValue: string,
  fieldHistory?: detailItemExtra['fieldHistory'],
) =>
  fieldHistory && hasExcomUpdate(fieldHistory, fieldName) ? (
    <FieldHistoryIndicator
      dataType="date"
      history={fieldHistory}
      fieldName={fieldName}
    />
  ) : (
    <span className="flex gap-2">
      <span>{fieldName}</span>
      <h4 className="m-0">
        {(fieldValue && dayjs(fieldValue).format('MMM-YY')) || '-'}
      </h4>
    </span>
  )

export const viewModesHandler: Record<FieldType, ViewModesHandler> = {
  text: (data, field, classNames, fieldHistory) =>
    detailItem(upperFirst(field.label), data[field.read_field_name], {
      detailClassname: 'self-start',
      classNames,
      fieldHistory,
    }),
  number: (data, field, _, fieldHistory, hasActualFields) => {
    const isDisabledImpactField = getIsDisabledImpactField(
      field,
      hasActualFields,
    )

    return numberDetailItem(
      field.label,
      data[field.read_field_name],
      field.data_type,
      fieldHistory,
      isDisabledImpactField,
      field.is_actual,
    )
  },
  decimal: (data, field, _, fieldHistory, hasActualFields) => {
    const isDisabledImpactField = getIsDisabledImpactField(
      field,
      hasActualFields,
    )

    return numberDetailItem(
      field.label,
      data[field.read_field_name],
      field.data_type,
      fieldHistory,
      isDisabledImpactField,
      field.is_actual,
    )
  },
  drop_down: (data, field, _, fieldHistory, hasActualFields) => {
    const value = data[field.read_field_name]
    const formattedValue = isBoolean(value)
      ? find(field.options, { id: data[field.write_field_name] })?.name || '-'
      : value

    const isDisabledImpactField = getIsDisabledImpactField(
      field,
      hasActualFields,
    )

    return detailItem(field.label, formattedValue, {
      fieldHistory,
      isDisabledImpactField,
      isActual: field.is_actual,
    })
  },
  boolean: (data, field, _, fieldHistory, hasActualFields) => {
    const className =
      field.section === 'Impact' ? 'col-span-full flex w-full' : ''
    const isDisabledImpactField = getIsDisabledImpactField(
      field,
      hasActualFields,
    )

    return booleanDetailItem(
      field.label,
      data[field.read_field_name],
      fieldHistory,
      className,
      isDisabledImpactField,
      field.is_actual,
    )
  },
  date: (data, field, _, fieldHistory) =>
    dateDetailItem(field.label, data[field.read_field_name], fieldHistory),
}
